/**
 * 🔴 read-tool-registry (US-AI-0271-S5 · @UC9400) — DANH SÁCH ĐÓNG các việc ĐỌC mà LLM được phép chọn.
 *
 * VÌ SAO TỒN TẠI: regex không bao giờ phủ hết ngôn ngữ tự nhiên (PO 17/07: "Nếu không có LLM thì hệ
 * thống sẽ ko bao giờ cover được tất cả những câu hỏi của user"). LLM lấp khe hở đó — nhưng KHÔNG
 * được tự do gọi API.
 *
 * 🔴 RANH GIỚI AN TOÀN (đây là toàn bộ lý do file này tồn tại):
 *  · LLM CHỌN TÊN trong danh sách hữu hạn + điền tham số. **KHÔNG BAO GIỜ tự dựng URL/endpoint.**
 *    Một LLM chọn URL tự do sẽ vượt qua tầng phân quyền: hỏi "quỹ Y còn bao nhiêu" trong khi user chỉ
 *    thuộc quỹ X ⇒ nó vui vẻ chọn /funds/Y/balance. Đây là vết đau THẬT của repo (IDOR C0d, audit
 *    E14-E20).
 *  · Service validate tên + tham số rồi mới gọi service THẬT qua ĐÚNG guard như GUI. LLM bịa fundId
 *    ⇒ guard trả 404 — không phải vì LLM ngoan, mà vì **đường ống không cho phép**.
 *  · LLM KHÔNG viết số. Nó chọn *cái gì cần đọc*, không nói *con số là bao nhiêu*. Service format
 *    bằng code, số lấy từ DB. LLM chạm dữ liệu tiền = có ngày nó làm tròn/bịa/"khoảng 5 triệu".
 *
 * 🔴 NĂNG LỰC = CODE, KHÔNG PHẢI CONFIG: thêm tool ⇒ thêm entry ở đây (qua PR + test). LLM chỉ chọn
 * được thứ ĐÃ TỒN TẠI — giống `AI_CAPABILITY_CATALOG` (admin toggle được, không bao giờ TẠO được).
 * LLM lấp được vô hạn CÁCH NÓI của một năng lực; nó KHÔNG lấp được năng lực CHƯA CÓ.
 *
 * 🔴 READ-ONLY, LUÔN BẬT (quyết định PO 17/07): registry này CHỈ có việc ĐỌC ⇒ không toggle per-group.
 * Tắt `member_list` chả bảo vệ gì (user vẫn xem danh sách trên GUI), chỉ làm trợ lý ngu đi vô nghĩa.
 * Cầu dao duy nhất là `ai_enabled` per-group đã có. Việc GHI TIỀN đi đường form (`AI_CAPABILITY_CATALOG`,
 * có toggle, có confirm) — TUYỆT ĐỐI không thêm write-tool vào file này.
 *
 * HÀM/HẰNG THUẦN (DP-3): KHÔNG DB, KHÔNG I/O, KHÔNG LLM.
 */

import type { QueryReportKind } from "./query-intent.js";

/**
 * Slot tham số một read-tool có thể cần.
 *
 * 🔴 `"fund"` thêm ở US-AI-0300-S02 — xem mục "QUỸ: LLM CHỌN, SERVER CHỐT" ở đầu file trước khi dùng.
 *
 * 🔴🔴 `"member"` thêm ở US-AI-0508 — và nó KHÔNG theo khuôn của `"fund"`. Đọc mục "NGƯỜI: LLM TRÍCH
 * LẠI TÊN, SERVER PHÂN GIẢI" ngay dưới `READ_TOOL_REGISTRY` trước khi động vào.
 */
export type ReadToolParam = "period" | "fund" | "member";

export interface ReadToolSpec {
  /** Tên LLM chọn. TRÙNG `QueryReportKind` ⇒ map thẳng sang đường đọc đã có, không lớp dịch thứ hai. */
  readonly name: QueryReportKind;
  /** Mô tả cho LLM — viết cho MÁY đọc: nói RÕ khi nào dùng, không quảng cáo. */
  readonly description: string;
  /** Tham số BẮT BUỘC. Thiếu ⇒ service hỏi lại (KHÔNG đoán — AI-INV). */
  readonly required: readonly ReadToolParam[];
  /**
   * Tham số TUỲ CHỌN — LLM điền được thì tốt, không thì service hỏi lại như cũ.
   * `fund` CỐ Ý ở đây chứ KHÔNG ở `required`: bắt buộc sẽ chặn cả câu hỏi khi LLM không chọn được quỹ.
   */
  readonly optional?: readonly ReadToolParam[];
  /**
   * 🔴🔴 US-AI-0425 (@UC9706) — tool này đọc dữ liệu CẤP TÀI KHOẢN, không thuộc quỹ nào.
   *
   * VÌ SAO CẦN CỜ NÀY: `AiQueryService.readQuery` trả clarify *"bạn muốn xem báo cáo của quỹ nào?"*
   * NGAY khi `scope === "all"`. Với câu *"tôi đang ở những nhóm nào"* — câu mở đầu tự nhiên nhất của
   * người mới — đó là một câu hỏi ngược vô nghĩa: họ hỏi vì **chưa biết mình có quỹ nào**.
   *
   * ⚠️ `accountLevel` KHÔNG nới quyền: dữ liệu vẫn là của CHÍNH actor (userId lấy từ token), và tool
   * cấp nhóm (`member_accounts`) cố ý **không** mang cờ này để vẫn đi qua scope + gác vai.
   */
  readonly accountLevel?: boolean;
}

/** Quỹ user THẤY ĐƯỢC (đã qua membership) — chỉ những quỹ này mới được bơm vào prompt. */
export interface AllowedFund {
  readonly id: string;
  readonly name: string;
}

/**
 * REGISTRY — nguồn sự thật DUY NHẤT cho những gì LLM được chọn ở nhánh đọc.
 *
 * 🔴 QUỸ: LLM CHỌN, SERVER CHỐT (US-AI-0300-S02 — QĐ PO 21/07).
 *
 * Bản trước ghi *"fundId CỐ Ý KHÔNG có mặt … cho LLM đưa fundId là mở đúng cánh cửa IDOR"*. Nay LLM
 * ĐƯỢC chọn quỹ, nhưng **cửa IDOR vẫn đóng** — và lý do KHÔNG phải "LLM ngoan", mà là đường ống:
 *
 *   Lớp 1 — Danh sách bơm vào prompt CHỈ gồm `AllowedFund[]` mà caller đã lọc theo membership.
 *           Quỹ user không có quyền **chưa bao giờ xuất hiện** ⇒ LLM không có gì để mà chọn nhầm.
 *   Lớp 2 — `pickAllowedFundId()`: id LLM trả về phải THUỘC đúng danh sách vừa bơm. Bịa UUID, đoán
 *           bừa, prefix, chuỗi rác ⇒ **BỎ**, coi như chưa chọn (fail-closed, không fail-open).
 *   Lớp 3 — `resolveFund` guard giữ NGUYÊN (defense-in-depth): kể cả hai lớp trên hỏng thì guard
 *           vẫn 404 như mọi đường đọc khác.
 *
 * ⇒ Khác biệt cốt lõi so với nỗi lo cũ: LLM **không tự dựng URL/endpoint** và **không đưa id tự do**;
 * nó chỉ CHỌN một phần tử trong tập ĐÓNG mà server đã duyệt. Cùng nguyên tắc với chip-ranking.
 * ⚠️ Gỡ Lớp 2 ⇒ `read-tool-fund-param.spec.ts` ĐỎ. Đừng gỡ.
 *
 * `groupId` VẪN KHÔNG có mặt — scope nhóm do server quyết tuyệt đối.
 */
export const READ_TOOL_REGISTRY: readonly ReadToolSpec[] = Object.freeze([
  Object.freeze({
    name: "balance" as const,
    description:
      "Số dư hiện tại của quỹ (còn bao nhiêu tiền). Dùng khi người dùng hỏi quỹ còn lại bao nhiêu.",
    required: Object.freeze([]),
    // 🔴 US-AI-0300-S02 — LLM chọn quỹ từ TẬP ĐÓNG (xem docblock đầu file). TUỲ CHỌN, không bắt buộc.
    optional: Object.freeze(["fund" as const]),
  }),
  Object.freeze({
    name: "unpaid" as const,
    description:
      "Danh sách thành viên CHƯA ĐÓNG tiền trong một tháng. Cần biết tháng. Dùng khi hỏi ai chưa nộp/còn nợ.",
    required: Object.freeze(["period" as const]),
    // 🔴 US-AI-0300-S02 — LLM chọn quỹ từ TẬP ĐÓNG (xem docblock đầu file). TUỲ CHỌN, không bắt buộc.
    optional: Object.freeze(["fund" as const]),
  }),
  Object.freeze({
    name: "income_total" as const,
    description: "Tổng số tiền THU ĐƯỢC trong một tháng. Cần biết tháng.",
    required: Object.freeze(["period" as const]),
    // 🔴 US-AI-0300-S02 — LLM chọn quỹ từ TẬP ĐÓNG (xem docblock đầu file). TUỲ CHỌN, không bắt buộc.
    optional: Object.freeze(["fund" as const]),
  }),
  // 🔴 Bug PO 20/07 — TỔNG CHI kỳ (đối xứng income). LLM map "tháng 7 nhóm tiêu bao nhiêu" → tool này.
  Object.freeze({
    name: "expense_total" as const,
    description: "Tổng số tiền quỹ đã CHI trong một tháng. Cần biết tháng.",
    required: Object.freeze(["period" as const]),
    // 🔴 US-AI-0300-S02 — LLM chọn quỹ từ TẬP ĐÓNG (xem docblock đầu file). TUỲ CHỌN, không bắt buộc.
    optional: Object.freeze(["fund" as const]),
  }),
  // 🔴 US-AI-0302 (audit 22/07) — `expense_list` ĐÃ có trong manifest + router + handler nhưng THIẾU
  // ở registry ⇒ nhánh LLM fallback KHÔNG BAO GIỜ chọn được nó. Năng lực "có" mà không với tới được.
  Object.freeze({
    name: "expense_list" as const,
    description:
      "LIỆT KÊ CHI TIẾT từng khoản chi trong một tháng (khác `expense_total` vốn chỉ trả MỘT con số tổng). Cần biết tháng.",
    required: Object.freeze(["period" as const]),
    optional: Object.freeze(["fund" as const]),
  }),
  // 🔴 US-AI-0295-S10 — giao dịch gần nhất (KHÔNG cần kỳ, khác `expense_list` hỏi CHI trong 1 tháng).
  Object.freeze({
    name: "recent_tx" as const,
    description:
      "Liệt kê các GIAO DỊCH gần nhất của quỹ (cả thu lẫn chi). KHÔNG cần tháng — là các giao dịch mới nhất.",
    required: Object.freeze([]),
    optional: Object.freeze(["fund" as const]),
  }),
  // 🔴 US-AI-0421 (@UC9433) — hồ sơ nhóm. Đọc thuần, KHÔNG tiền.
  //
  // Mô tả CỐ Ý nói rõ nó KHÁC `member_list`: LLM chọn tool theo mô tả, nên hai mô tả mờ nhau là hai
  // tool cướp traffic của nhau — sẹo `vote-open-router.test.ts`.
  Object.freeze({
    name: "group_profile" as const,
    description:
      "THÔNG TIN CHUNG của nhóm: tên, mục đích, số thành viên, vai trò của chính người hỏi. KHÔNG phải danh sách tên từng người (cái đó là `member_list`). KHÔNG cần tháng.",
    required: Object.freeze([]),
  }),
  // 🔴 US-AI-0421 đợt 2 (@UC9433) — link mời đang hiệu lực. Đọc thuần, KHÔNG tiền.
  //
  // ⚠️ Đọc được KHÔNG có nghĩa ai cũng đọc được: endpoint gốc gác OWNER/ADMIN, và handler ở service
  // giữ nguyên guard đó. T0 nói về "không cần người xác nhận", không phải "không cần quyền".
  Object.freeze({
    name: "invite_links" as const,
    description:
      "Các LINK MỜI vào nhóm đang còn hiệu lực (còn hạn hay đã hết, hết hạn khi nào). KHÔNG phải danh sách người đã vào nhóm (cái đó là `member_list`). KHÔNG cần tháng.",
    required: Object.freeze([]),
  }),
  // ── US-AI-0425 (@UC9706) — dữ liệu của CHÍNH người dùng ─────────────────────────────────────
  Object.freeze({
    name: "my_groups" as const,
    description:
      "Các NHÓM mà chính người hỏi đang tham gia (tên nhóm, vai trò của họ). Dùng cho câu 'tôi đang ở những nhóm nào'. KHÁC `group_profile` (hồ sơ của MỘT nhóm đang mở). Chạy được cả khi người hỏi chưa có quỹ nào. KHÔNG cần tháng.",
    required: Object.freeze([]),
    accountLevel: true,
  }),
  Object.freeze({
    name: "my_profile" as const,
    description:
      "HỒ SƠ của chính người hỏi: tên hiển thị, email đã gắn hay chưa. KHÔNG phải danh sách nhóm (cái đó là `my_groups`). KHÔNG cần tháng.",
    required: Object.freeze([]),
    accountLevel: true,
  }),
  Object.freeze({
    name: "my_linked_accounts" as const,
    description:
      "Các CÁCH ĐĂNG NHẬP đã gắn với tài khoản của chính người hỏi (Google/Apple/Zalo…) và đã đặt mật khẩu chưa. CHỈ tên nhà cung cấp — KHÔNG token, KHÔNG mật khẩu. KHÔNG cần tháng.",
    required: Object.freeze([]),
    accountLevel: true,
  }),
  Object.freeze({
    name: "member_list" as const,
    description:
      "Danh sách thành viên của nhóm/quỹ (có những ai). KHÔNG cần tháng — là trạng thái hiện tại.",
    required: Object.freeze([]),
  }),
  // ── US-AI-0508 (@UC0060 @UC0055 @UC9659 @UC9596) — HAI tool trỏ vào MỘT NGƯỜI ────────────────
  //
  // 🔴 Ô này từng mang ghi chú *"`member_accounts` CỐ Ý CHƯA SHIP"* với đúng hai lý do, và task này
  // gỡ CẢ HAI (giữ lại đây để không ai "sửa ngược"):
  //  1. Registry chưa có slot trỏ vào MỘT chỗ ngồi (`ReadToolParam` chỉ `period`/`fund`) — nay có
  //     `"member"`, và nó mang TÊN chứ không mang id (xem khối "NGƯỜI: LLM TRÍCH LẠI TÊN" dưới).
  //  2. Đo thật lúc đó: câu ví dụ đi qua `routeTurn` ra `answer` fallback chung chung ⇒ khai vào
  //     manifest là **quảng cáo rồi từ chối**. Nay `query-intent.ts` có nhánh thật và
  //     `capability-routable.spec.ts` canh đúng câu ví dụ của hai năng lực mới.
  //
  // ⚠️ RBAC của `GET /groups/:id/members/:memberId/accounts` ĐÃ NỚI 16/08 (`US-E24.F8-0420`): MỌI
  // thành viên ACTIVE đọc được (guard chỉ còn `GroupRoleGuard`, không `@RequireGroupRoles`). Handler
  // AI gác theo HIỆN TRẠNG đó — `resolveFund` đã chốt "phải là member ACTIVE của nhóm" — chứ KHÔNG
  // chép lại dòng backlog cũ vốn ghi O/A-only.
  Object.freeze({
    name: "member_contributions" as const,
    description:
      "MỘT người đã đóng bao nhiêu và còn thiếu bao nhiêu, tính trên TOÀN NHÓM (mọi quỹ). Dùng khi câu hỏi NÊU TÊN một người: 'chị Lan đã đóng bao nhiêu rồi', 'lịch sử đóng góp của Minh'. KHÁC `member_list` (chỉ liệt kê nhóm có những ai, không có tiền) và KHÁC `unpaid` (danh sách CẢ NHÓM chưa đóng trong MỘT tháng). Cần tên người. KHÔNG cần tháng.",
    required: Object.freeze(["member" as const]),
  }),
  Object.freeze({
    name: "member_accounts" as const,
    description:
      "Những NGƯỜI đang đại diện / đứng tên cho MỘT suất trong nhóm (ví dụ suất của một đứa trẻ có thể có hai phụ huynh). Dùng khi câu hỏi NÊU TÊN một suất: 'ai đại diện cho suất của bé An'. KHÁC `member_list` (liệt kê các SUẤT của nhóm, không cho biết ai đứng sau một suất). KHÔNG có tiền. Cần tên người. KHÔNG cần tháng.",
    required: Object.freeze(["member" as const]),
  }),
  // 🔴🔴 US-AI-0509 (@UC0074 @UC9591 @UC9593) — DÒNG THỜI GIAN CỦA CẢ NHÓM.
  //
  // Mô tả dưới đây CỐ Ý gọi tên `recent_tx` và nói thẳng phạm vi của từng cái. Đây là cặp dễ lẫn
  // NHẤT trong registry: cả hai đều là "kể lại chuyện vừa xảy ra". Nếu để hai mô tả mờ nhau thì LLM
  // chọn theo cảm tính và hai tool cướp traffic của nhau — đúng sẹo `vote-open-router.test.ts`.
  // Vế phân biệt PHẢI có mặt trong chữ: feed kể cả việc **KHÔNG dính tiền** (ai vào/rời nhóm), thứ
  // mà sổ cái của một quỹ không bao giờ có.
  //
  // ⚠️ KHÔNG khai `optional: ["period"]`: `ActivityFeedService` không nhận kỳ. Phơi một tham số mà
  // service không đọc là "toggle nói dối" (§3.8) — LLM sẽ điền, và không gì xảy ra.
  // ⚠️ Bộ lọc Thu/Chi (`?kind=income|expense`) của endpoint gốc CỐ Ý CHƯA phơi: nó đòi một slot
  // `ReadToolParam` thứ tư, ngoài phạm vi US-AI-0509. Tool không lọc vẫn trả lời đúng câu hỏi chính;
  // nợ ghi trong PR, không giấu trong code.
  Object.freeze({
    name: "group_activity" as const,
    description:
      "DÒNG THỜI GIAN HOẠT ĐỘNG của CẢ NHÓM: ai vừa vào nhóm, ai rời nhóm, đợt thu nào vừa mở, khoản chi nào vừa được duyệt/từ chối, ai vừa nộp tiền. Dùng cho câu hỏi về NHÓM dạo này có chuyện gì: 'nhóm mình dạo này có hoạt động gì', 'ai vừa vào nhóm'. KHÁC `recent_tx` (chỉ GIAO DỊCH trong sổ của MỘT QUỸ) — tool này kể cả những việc KHÔNG dính tiền. KHÁC `member_list` (danh sách thành viên hiện tại) và `group_profile` (hồ sơ nhóm). KHÔNG cần tháng.",
    required: Object.freeze([]),
  }),
]);

/**
 * 🔴🔴 NGƯỜI: LLM TRÍCH LẠI TÊN, SERVER PHÂN GIẢI (US-AI-0508 · @UC9706) — ĐỌC TRƯỚC KHI SỬA.
 *
 * Slot `member` **cố ý KHÔNG theo khuôn `fund`**. Khuôn quỹ có ba lớp và lớp 1 là *"bơm danh sách
 * vào prompt"*. Áp nguyên khuôn đó cho NGƯỜI là sai ở đúng chỗ nguy hiểm nhất:
 *
 *  · `pii-scrubber.ts` chỉ bắt **email · điện thoại · số tài khoản theo MẪU**. Tên người KHÔNG phải
 *    một mẫu regex ⇒ nó **không** scrub được, và không thể.
 *  · Bơm roster ⇒ **đẩy cả danh bạ 40 phụ huynh sang LLM MỖI LƯỢT hỏi**, kể cả những lượt chẳng liên
 *    quan tới ai. CLAUDE.md §4: *PII công dân VN không rời hệ thống*.
 *
 * ⇒ `member` mang **mảnh tên mà chính người dùng ĐÃ GÕ trong câu hỏi** ("chị Lan"); LLM chỉ TRÍCH
 * LẠI. **Không một dữ liệu mới nào rời hệ thống** — cái tên đó vốn đã nằm trong câu gửi lên.
 *
 * Đổi lại, hai lớp chặn của khuôn quỹ được thay bằng hai lớp KHÁC, mạnh hơn cho ca này:
 *  · Phân giải tên → `groupMember.id` THẬT là việc của `GroupsService.resolveMemberByName` (tầng API,
 *    có DB) — **CÙNG cửa** đường GHI dùng (`AiProposalService.resolveMemberOrThrow`). Không resolver
 *    thứ hai (CLAUDE.md §3.8).
 *  · Ba trạng thái, và hai trong ba KHÔNG chạm dữ liệu: `resolved` ⇒ chạy · `ambiguous` ⇒ **CLARIFY
 *    nêu ứng viên, TUYỆT ĐỐI không đoán** · `not_found` ⇒ nói thật. Đây đúng dòng CLAUDE.md §4 đã ghi
 *    thành bảng: *"Đoán memberId khi 0 hoặc ≥2 khớp"* là việc AI KHÔNG ĐƯỢC làm.
 *
 * ⚠️ Guard `member-slot-read-tools.spec.ts` khoá điều này bằng phép đo: nhét roster vào prompt ⇒ ĐỎ.
 * Và `buildReadToolSystemPrompt` cố ý **không có tham số nào cho danh sách người** — chặn ở CHỮ KÝ,
 * không chỉ bằng lời hứa trong comment.
 */

/** Tên hợp lệ — fail-closed: LLM bịa tên ngoài set này ⇒ service nói thật "chưa hỗ trợ". */
const VALID_TOOL_NAMES: ReadonlySet<string> = new Set(READ_TOOL_REGISTRY.map((t) => t.name));

export function isReadToolName(name: string): name is QueryReportKind {
  return VALID_TOOL_NAMES.has(name);
}

export function findReadTool(name: string): ReadToolSpec | null {
  return READ_TOOL_REGISTRY.find((t) => t.name === name) ?? null;
}

/**
 * System prompt cho LLM chọn tool. Nói RÕ ranh giới để nó không cố vượt:
 * không bịa số, không bịa tool, thiếu thông tin thì nói thiếu.
 */
export function buildReadToolSystemPrompt(funds: readonly AllowedFund[] = []): string {
  const lines = READ_TOOL_REGISTRY.map(
    (t) =>
      `- ${t.name}: ${t.description}${t.required.length > 0 ? ` (bắt buộc: ${t.required.join(", ")})` : ""}`,
  );
  // 🔴 CHỈ bơm quỹ caller đã lọc theo membership (Lớp 1). Rỗng ⇒ không có khối quỹ, hành vi như cũ.
  const fundBlock =
    funds.length > 0
      ? [
          "",
          "Quỹ người dùng đang có (CHỈ được chọn trong danh sách này):",
          ...funds.map((f) => `- ${f.id} = ${f.name}`),
          "",
          "QUY TẮC CHỌN QUỸ:",
          "5. Nếu câu hỏi nhắc tới một quỹ, hãy chọn `fund` là ID TRONG danh sách trên.",
          "   Hiểu cả cách gọi tắt, sai chính tả, không dấu (vd 'quy tet' = 'Quỹ Tết 2026').",
          // 🔴 US-AI-0315-S04 (C2 · @gate ai) — quy tắc 6 CŨ dạy: "Mơ hồ giữa nhiều quỹ ⇒ chọn quỹ
          // HỢP LÝ NHẤT (quỹ vừa được nhắc trong hội thoại trước, rồi tới quỹ mới nhất). KHÔNG hỏi
          // lại người dùng." Sai ba tầng:
          //  (a) ngược bất biến của cả hệ ("mơ hồ ⇒ CLARIFY, không đoán");
          //  (b) ngược chính code bên cạnh — `buildFundChoices` HỎI LẠI và phát
          //      `pendingQuery{awaiting:"fund"}` khi mơ hồ, tức hai tầng nói hai điều khác nhau;
          //  (c) LLM KHÔNG có dữ liệu để đoán đúng: prompt này chỉ đưa DANH SÁCH quỹ, không có lịch
          //      sử hội thoại — bảo nó chọn "hợp lý nhất" là bảo nó bịa.
          // Đọc số dư SAI QUỸ vẫn là nói dối về tiền, kể cả khi chỉ đọc.
          "6. Mơ hồ giữa nhiều quỹ (câu nói khớp từ hai quỹ trở lên, hoặc không rõ quỹ nào) ⇒",
          "   BỎ TRỐNG `fund`. Đừng chọn đại và đừng tự hỏi lại — hệ thống sẽ hỏi người dùng chọn quỹ.",
          "7. Không quỹ nào trong danh sách phù hợp ⇒ BỎ TRỐNG `fund`. TUYỆT ĐỐI không bịa ID.",
        ]
      : [];
  return [
    "Bạn là bộ định tuyến câu hỏi cho ứng dụng quản lý quỹ nhóm tiếng Việt.",
    "Nhiệm vụ DUY NHẤT: chọn MỘT công cụ đọc dữ liệu phù hợp với câu hỏi của người dùng.",
    "",
    "Công cụ có sẵn:",
    ...lines,
    "",
    "QUY TẮC BẮT BUỘC:",
    "1. CHỈ chọn công cụ trong danh sách trên. TUYỆT ĐỐI không bịa tên công cụ khác.",
    "2. KHÔNG trả lời câu hỏi bằng số liệu. Bạn KHÔNG biết số dư hay tên thành viên — hệ thống sẽ đọc.",
    "3. Câu hỏi không khớp công cụ nào (hỏi chuyện khác, xã giao, hỏi việc ghi/sửa/xoá dữ liệu)",
    "   ⇒ KHÔNG gọi công cụ nào cả.",
    "4. Công cụ cần tháng mà người dùng chưa nêu ⇒ vẫn chọn công cụ đó nhưng BỎ TRỐNG tháng;",
    "   hệ thống sẽ hỏi lại. KHÔNG được tự đoán tháng.",
    // 🔴 US-AI-0508 — slot `member`. Bạn KHÔNG có danh sách thành viên (cố ý: xem docblock
    // "NGƯỜI: LLM TRÍCH LẠI TÊN"), nên luật ở đây là TRÍCH LẠI, không phải CHỌN.
    "8. Công cụ cần tên người (`member`): CHÉP LẠI ĐÚNG mảnh tên người dùng vừa nêu trong câu hỏi",
    "   (ví dụ hỏi \"chị Lan đã đóng bao nhiêu\" ⇒ member = \"chị Lan\"). Bạn KHÔNG có danh sách",
    "   thành viên và TUYỆT ĐỐI KHÔNG được bịa tên. Người dùng chưa nêu tên ai ⇒ BỎ TRỐNG `member`;",
    "   hệ thống sẽ hỏi lại. Câu hỏi về CHÍNH người đang hỏi (\"tôi\", \"mình\") KHÔNG phải tên người.",
    ...fundBlock,
  ].join("\n");
}

/**
 * 🔴 LỚP CHẶN CUỐI (US-AI-0300-S02) — id quỹ LLM trả về CHỈ được nhận khi thuộc ĐÚNG danh sách đã bơm.
 *
 * FAIL-CLOSED: mọi trường hợp còn lại ⇒ `null` = "LLM chưa chọn được" ⇒ service hỏi lại như cũ.
 * Bao gồm: id quỹ NHÓM KHÁC (IDOR), UUID bịa, chuỗi rác, prefix, danh sách rỗng.
 * So khớp CHÍNH XÁC — không prefix/substring, vì `startsWith` sẽ nhận nhầm id cắt ngắn.
 *
 * ⚠️ Gỡ hàm này = mở lại cửa IDOR mà docblock đầu file tuyên bố đang đóng.
 */
export function pickAllowedFundId(
  candidate: string | null | undefined,
  funds: readonly AllowedFund[],
): string | null {
  if (typeof candidate !== "string" || candidate.length === 0) return null;
  return funds.some((f) => f.id === candidate) ? candidate : null;
}

/** Tool-schema provider-agnostic (map thẳng sang `LlmInput.tools`). */
export function readToolSchemas(): readonly {
  readonly name: string;
  readonly description: string;
  readonly inputSchema: Readonly<Record<string, unknown>>;
}[] {
  return READ_TOOL_REGISTRY.map((t) => ({
    name: t.name,
    description: t.description,
    inputSchema: {
      type: "object",
      properties: {
        period: {
          type: "string",
          description: "Tháng dạng YYYY-MM (ví dụ 2026-07). Bỏ trống nếu người dùng chưa nêu.",
        },
        // 🔴 US-AI-0311 (vá B1 — QĐ PO #2): tool nhận quỹ ⇒ phơi `fund` cho LLM CHỌN. Trước đây registry
        // khai `optional:['fund']` + có `pickAllowedFundId` nhưng schema KHÔNG có `fund` (+ additionalProperties
        // false) ⇒ LLM không bao giờ trả được ⇒ năng lực chọn quỹ CHẾT. id trả về vẫn qua pickAllowedFundId.
        ...(t.optional?.includes("fund")
          ? {
              fund: {
                type: "string",
                description:
                  "ID quỹ (CHỈ chọn trong danh sách quỹ đã cho ở system prompt). Bỏ trống nếu không rõ.",
              },
            }
          : {}),
        // 🔴 US-AI-0508 — slot NGƯỜI, phơi ĐÚNG cho tool khai nó. Đây là TÊN, KHÔNG phải id: không có
        // danh sách người nào được bơm vào prompt, nên không có gì để "chọn" — chỉ có gì để TRÍCH LẠI.
        ...(t.required.includes("member") || t.optional?.includes("member")
          ? {
              member: {
                type: "string",
                description:
                  "Tên người mà câu hỏi vừa nêu — CHÉP LẠI nguyên văn từ câu của người dùng (vd 'chị Lan'). TUYỆT ĐỐI KHÔNG bịa tên, không suy ra tên từ ngữ cảnh. Chưa nêu tên ⇒ bỏ trống.",
              },
            }
          : {}),
      },
      // `required: []` CÓ CHỦ Ý: thiếu tháng ⇒ hệ thống HỎI LẠI, không phải LLM tự điền bừa
      // (AI-INV: thà hỏi lại còn hơn đoán sai). Ép required ⇒ mời LLM bịa tháng cho đủ schema.
      required: [],
      additionalProperties: false,
    },
  }));
}
